import {useEffect, useState} from 'react';
import {MODAL_TYPE, showModal} from '../../shared/components/modals/generic_modal_conductor';
import {
	Contact,
	ContactsFetchError,
	EnabledInvoiceIntegrations,
	fetchEnabledIntegrationContacts,
} from './InvoiceIntegrationsUtils';

/**
 * Loads the contacts of every invoicing integration the company has connected, for the Bill To modals.
 *
 * On failure, closes the calling modal (`closeModal` pops it off the conductor stack) and shows the
 * non-critical error modal in its place, so the user is never left on an empty or spinning list.
 * Nothing happens if the modal has already been closed by the time the request settles.
 */
export const useEnabledIntegrationContacts = (
	enabled: EnabledInvoiceIntegrations | null | undefined,
	closeModal: () => void
): {contacts: Contact[]; isLoading: boolean} => {
	const [contacts, setContacts] = useState<Contact[]>([]);
	const [isLoading, setIsLoading] = useState(true);
	const newXeroEnabled = enabled?.newXeroEnabled;
	const economicEnabled = enabled?.economicEnabled;
	const quickbooksEnabled = enabled?.quickbooksEnabled;

	useEffect(() => {
		let cancelled = false;
		fetchEnabledIntegrationContacts({newXeroEnabled, economicEnabled, quickbooksEnabled})
			.then(allContacts => {
				if (!cancelled) {
					setContacts(allContacts);
					setIsLoading(false);
				}
			})
			.catch((error: unknown) => {
				if (!cancelled) {
					closeModal();
					showModal({
						type: MODAL_TYPE.MUTATION_ERROR_NON_CRITICAL,
						correlationId: error instanceof ContactsFetchError ? error.correlationId : undefined,
					});
				}
			});
		return () => {
			cancelled = true;
		};
		// closeModal is deliberately left out: it is a fresh bound function on every conductor render,
		// and the fetch must run once per mount.
	}, [newXeroEnabled, economicEnabled, quickbooksEnabled]);

	return {contacts, isLoading};
};
